import { Space } from 'antd';
import { DeviceControls, DeviceHistory, GroupSettingsDeviceList, VideoPlayer } from 'components';
import LocationSetter from 'components/AlertCreator/components/LocationSetter';
import AudioManager from 'components/AudioManager/AudioManager';
import FullScreenMap from 'components/FullScreenMap';
import BaseModal from 'components/Modals/BaseModal';
import TextToggle from 'components/UI/TextToggle';
import { usePerms } from 'contexts/PermsContext';
import layout from 'data/DeviceControls.json';
import { DeviceIsDeviceType } from 'features/integrations/utils/deviceComparisionUtils';
import {
  defineGroupType,
  isDeviceTypeAllowed,
} from 'features/integrations/utils/deviceGroupRulesUtils';
import L from 'leaflet';
import { useEffect, useState } from 'react';
import { Button } from 'react-bootstrap';
import { Marker, Polyline } from 'react-leaflet';
import { sendGroupCommand } from 'services/devices/commands/commands.service';
import {
  useGetConnectedDevicesQuery,
  useKillswitchDeviceMutation,
} from 'services/devices/devices.api';
import { getDynamicDeviceIcon } from 'utils/iconUtils';
import DevicePinPopup from './components/DevicePinPopup';

/**
 * @param {{group: object, onSelectDevice: (id: number), onClose?: ()}}
 */
export default function DeviceGroupSettings({ group, onSelectDevice }) {
  const {
    data: connectedDevices = [],
    isLoading: connectedDevicesLoading,
    isError: connectedDevicesError,
  } = useGetConnectedDevicesQuery();
  const [killswitchDevice, { isLoading: isKilling }] = useKillswitchDeviceMutation();

  const perms = usePerms();

  const groupDevices =
    connectedDevices?.filter((_device) => group?.devices?.includes(_device.id)) || [];
  const groupType = defineGroupType(group?.devices, connectedDevices, group?.type);
  const macroEligible =
    groupDevices.length > 0 &&
    groupDevices.every((device) => isDeviceTypeAllowed(device?.device_type, groupType));

  const [avgPosition, setAvgPosition] = useState();
  const [virtualDevice, setVirtualDevice] = useState({ device_type: '' });
  const [showVideo, setShowVideo] = useState(false);
  const [displayMessage, setDisplayMessage] = useState();
  const [sending, setSending] = useState(false);
  const [audioManager, setAudioManager] = useState(false);
  const [audioOverride, setAudioOverride] = useState(null);

  const getPASirenIds = () => {
    return connectedDevices
      ?.filter(
        (_device) =>
          group.devices?.includes(_device.id) &&
          _device.device_type === 'Technology Bridge_Siren/Giant Voice' &&
          _device?.camera,
      )
      .map((_device) => _device.id);
  };

  //TODO: Figure out how to determine the current status of the group
  //TODO: Implement camera toggle on map

  useEffect(() => {
    const devices = groupDevices;

    if (devices.length < 1) {
      setAvgPosition({
        lat: 29.798707863118377,
        lng: -98.29839841752587,
      });
    } else {
      let avgPos = { lat: 0, lng: 0 };
      devices.forEach(
        (device) =>
          (avgPos = {
            lat: avgPos.lat + Number(device.location.lat),
            lng: avgPos.lng + Number(device.location.lng),
          }),
      );

      setAvgPosition({
        lat: avgPos.lat / devices.length,
        lng: avgPos.lng / devices.length,
      });
    }

    const selectedType = groupType || group?.type || '';
    const device = devices.find((device) => device.device_type === selectedType) || devices[0];

    const data = connectedDevices
      ?.filter(
        (_device) => group.devices?.includes(_device.id) && !DeviceIsDeviceType(_device, 'Stage'),
      )
      .map((_device) => _device?.data)
      .flatMap((v) => v)
      .sort((a, b) => b.ts - a.ts);

    setVirtualDevice({
      ...device,
      data: data,
      device_type: selectedType || device?.device_type || '',
    });
  }, [group, connectedDevices, groupDevices, groupType]);

  const typeToPermMap = {
    'Technology Bridge_Flashing Lights': 'RescAlertDevicesTechnologyBridge_FlashingLights',
    'Technology Bridge_Siren/Giant Voice': 'RescAlertDevicesTechnologyBridge_Siren-GiantVoice',
  };

  const createEditPermission = () => {
    return group.type
      ? perms.testPermission(
          [8, 9, 10, 11, 12],
          [`Create-Edit${typeToPermMap[group.type]}Connections`],
        )
      : true;
  };

  const renderDevice = (deviceId, index) => {
    const device = connectedDevices?.find((device) => device.id === deviceId);

    if (device)
      return (
        <Marker
          key={deviceId + '' + index}
          position={device.location}
          icon={getDynamicDeviceIcon(device, 'icon')}
        >
          <DevicePinPopup device={device} onSelect={onSelectDevice} />
        </Marker>
      );
  };

  const renderLine = (line) => {
    return (
      <>
        <Polyline positions={line.coordinates} color={'#FF0000'} />

        {line.coordinates.map((point) => (
          <Marker
            position={point}
            icon={
              new L.DivIcon({
                className: 'custom-x-icon',
                html: `<div style="color: red; font-size: 30px; position: absolute; top: -4px; left: -2px;">X</div>`,
                iconSize: [20, 20],
                iconAnchor: [10, 16],
              })
            }
          />
        ))}
      </>
    );
  };

  const switchMaintenanceMode = async (device, kill_switchValue) => {
    if (!perms.isMaintenanceOperator()) return;

    try {
      await killswitchDevice({ id: device.id, state: kill_switchValue }).unwrap();
    } catch (err) {
      console.error('Error sending group command:', err.message);
      setDisplayMessage({
        type: 'error',
        message: 'Failed to send command!',
      });
    }
  };

  const groupSwitchMaintenanceMode = () => {
    const devices = getGroupDevices();
    //If any device has a kill switch enabled
    if (devices.some((device) => device.kill_switch === 1)) {
      //Disable all kill switches
      devices
        .filter((_device) => _device.kill_switch === 1) //Isolate only the enabled ones
        .map((_device) => switchMaintenanceMode(_device, 0));
    } else {
      //Enable all kill switches
      devices.map((_device) => switchMaintenanceMode(_device, 1));
    }
  };

  const getGroupDevices = () => {
    return groupDevices;
  };

  const deviceControlListener = async (evt, data) => {
    switch (evt) {
      case 'update':
        setVirtualDevice(data);
        return;
      case 'submit':
        try {
          setSending(true);
          await sendGroupCommand(group.id, data.status, data.comments);
          setSending(false);
        } catch (err) {
          console.error('Error sending device command:', err.message);
          setDisplayMessage({
            type: 'error',
            message: 'Failed to send command!',
          });
        }

        //Disable on close when command is sent.
        // onClose();
        return;
      case 'cancel-audio':
        try {
          await sendGroupCommand(group.id, 7, '');
        } catch (err) {
          console.error('Error sending device command:', err.message);
          setDisplayMessage({
            type: 'error',
            message: 'Failed to send command!',
          });
        }
        return;
      case 'audio-manager':
        setAudioManager(true);
        return;
      default:
        return;
    }
  };

  const onSetTempAudio = (fileName) => {
    setAudioOverride({
      id: -1,
      name: fileName,
      displayName: fileName,
      relay_id: 1,
      device_ids: null,
    });

    setAudioManager(false);
  };

  return (
    <div className="d-flex flex-column min-h-75vh gap-2 overflow-y-auto overflow-x-hidden">
      {/* Top */}
      <div className="d-flex flex-column flex-lg-row flex-fill flex-basis-0 gap-2">
        <div className="d-flex flex-column flex-fill flex-basis-0">
          {avgPosition && (
            <div className="d-flex flex-fill flex-column min-h-250">
              {!showVideo && (
                <FullScreenMap center={[avgPosition?.lat, avgPosition?.lng]} zoom={10}>
                  <LocationSetter
                    center={[avgPosition?.lat || 0, avgPosition?.lng || 0]}
                    zoom={10}
                  />
                  {/* check for coordinates existing before mapping */}
                  {group?.coordinates && group?.coordinates.map(renderLine)}

                  {group?.devices && group.devices.map(renderDevice)}
                </FullScreenMap>
              )}

              {showVideo && group?.auxDevices?.length > 0 && (
                <div className="d-flex flex-fill">
                  <VideoPlayer
                    src={`${process.env.PUBLIC_API_URL}/camera_v2/${group.auxDevices[0]}/hls/index.m3u8`}
                  />
                </div>
              )}

              {group?.auxDevices?.length > 0 &&
                perms.testPermission(
                  [8, 9, 10, 11, 12],
                  [`ViewExacqVisionOverwatchVMSConnections`],
                ) && (
                  <Space>
                    <TextToggle
                      options={['Map', 'Video']}
                      value={showVideo}
                      setValue={setShowVideo}
                    />
                  </Space>
                )}
            </div>
          )}

          {macroEligible &&
            perms.isMaintenanceOperator() &&
            layout[virtualDevice.device_type.toLowerCase()] && (
              <Button
                variant={`${
                  getGroupDevices().some(
                    (device) => device.kill_switch || device.data?.status === -1,
                  )
                    ? 'warning'
                    : 'primary'
                } mt-1 border border-black`}
                onClick={groupSwitchMaintenanceMode}
              >
                {getGroupDevices().some(
                  (device) => device.kill_switch || device.data?.status === -1,
                )
                  ? 'Disable Maintenance Mode'
                  : 'Enable Maintenance Mode'}
              </Button>
            )}
        </div>

        {macroEligible &&
          virtualDevice &&
          createEditPermission() &&
          layout[virtualDevice.device_type.toLowerCase()] && (
            <div className="flex-fill flex-basis-0">
              <DeviceControls
                device={virtualDevice}
                devices={getGroupDevices()}
                displayMessage={displayMessage}
                on={deviceControlListener}
                sending={sending}
                audioOverride={audioOverride}
              />
            </div>
          )}
      </div>

      {/* Bottom */}
      <div className="d-flex flex-column flex-lg-row flex-fill flex-basis-0 gap-2">
        {macroEligible && layout[virtualDevice.device_type.toLowerCase()] && (
          <div className="d-flex flex-fill flex-basis-0">
            <DeviceHistory
              title={'Group Device History'}
              data={virtualDevice?.data}
              deviceType={virtualDevice?.device_type}
            />
          </div>
        )}

        <div className="d-flex flex-fill flex-basis-0">
          <GroupSettingsDeviceList
            devices={connectedDevices?.filter((_device) => group.devices?.includes(_device.id))}
            onSelect={onSelectDevice}
            highlightIds={
              virtualDevice?.audio?.device_ids ? [...virtualDevice.audio.device_ids] : []
            }
          />
        </div>
      </div>

      {/* Audio Manager */}
      <BaseModal
        show={perms.testPermission([], ['ViewSirenAudioFiles']) && audioManager}
        onHide={() => setAudioManager(false)}
      >
        <AudioManager
          deviceIds={getPASirenIds()}
          onClose={() => setAudioManager(false)}
          onPlayTemp={onSetTempAudio}
        />
      </BaseModal>
    </div>
  );
}
